<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  
  export let isStationaryAdf: boolean | null = null;
  export let isStationaryKpss: boolean | null = null;
  export let isOpen: boolean = false;
  
  const dispatch = createEventDispatcher();
  
  // Определение состояния стационарности
  $: isStationaryConsensus = isStationaryAdf && isStationaryKpss;
  $: isNonStationaryConsensus = isStationaryAdf === false && isStationaryKpss === false;
  $: isMixedResults = isStationaryAdf !== isStationaryKpss && isStationaryAdf !== null && isStationaryKpss !== null;
  
  // Закрытие информации о стационарности
  function closeGuide() {
    isOpen = false;
    dispatch('close');
  }
  
  // Функция для отправки события "применить дифференцирование"
  function applyDifferencing() {
    dispatch('applyDifferencing');
  }
</script>

<div class="stationarity-guide card mb-3" class:d-none={!isOpen}>
  <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
    <div>
      <i class="bi bi-info-circle me-2"></i>
      Анализ стационарности временного ряда
    </div>
    <button type="button" class="btn-close btn-close-white" on:click={closeGuide} aria-label="Закрыть"></button>
  </div>
  
  <div class="card-body">
    {#if isStationaryAdf === null || isStationaryKpss === null}
      <p class="mb-3">Оценка стационарности временного ряда еще не выполнена.</p>
      <p class="mb-0">Стационарность - важное свойство для успешного прогнозирования. Стационарный ряд имеет постоянные статистические свойства во времени (среднее, дисперсию, автокорреляцию).</p>
    {:else if isStationaryConsensus}
      <div class="alert alert-success mb-3">
        <i class="bi bi-check-circle-fill me-2"></i>
        <strong>Ряд стационарен</strong> согласно тестам ADF и KPSS.
      </div>
      <p class="mb-3">Оба статистических теста подтверждают стационарность ряда. Это идеальный случай для применения методов прогнозирования ARIMA.</p>
      <p class="mb-0"><i class="bi bi-lightbulb me-1"></i> <strong>Рекомендация:</strong> Можно приступать к прогнозированию. Для этого ряда хорошо подойдут модели ARMA и ARIMA с низким параметром дифференцирования (d=0 или d=1).</p>
    {:else if isNonStationaryConsensus}
      <div class="alert alert-warning mb-3">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        <strong>Ряд не стационарен</strong> согласно тестам ADF и KPSS.
      </div>
      <p class="mb-3">Оба статистических теста указывают на нестационарность ряда. Для корректного прогнозирования необходимо преобразовать ряд путем дифференцирования.</p>
      <div class="d-flex mb-3">
        <button class="btn btn-primary" on:click={applyDifferencing}>
          <i class="bi bi-arrow-repeat me-2"></i>
          Применить дифференцирование
        </button>
      </div>
      <p class="mb-0"><i class="bi bi-lightbulb me-1"></i> <strong>Рекомендация:</strong> Используйте параметр d=1 или d=2 в модели ARIMA, чтобы учесть нестационарность ряда.</p>
    {:else if isMixedResults}
      <div class="alert alert-info mb-3">
        <i class="bi bi-question-circle-fill me-2"></i>
        <strong>Неоднозначные результаты</strong> тестов стационарности.
      </div>
      <div class="mb-3">
        <div>Тест ADF: <strong>{isStationaryAdf ? 'Стационарен' : 'Не стационарен'}</strong></div>
        <div>Тест KPSS: <strong>{isStationaryKpss ? 'Стационарен' : 'Не стационарен'}</strong></div>
      </div>
      <p class="mb-3">Разногласие в результатах тестов может указывать на сложную структуру ряда. Возможно, ряд имеет тренд или сезонность, которые требуют дополнительного анализа.</p>
      <p class="mb-0"><i class="bi bi-lightbulb me-1"></i> <strong>Рекомендация:</strong> Попробуйте сезонное дифференцирование или логарифмирование ряда. Для ARIMA рекомендуется использовать d=1 и проверить результаты на остатках модели.</p>
    {/if}
  </div>
  
  <div class="card-footer">
    <div class="accordion" id="stationarityInfoAccordion">
      <div class="accordion-item">
        <h2 class="accordion-header" id="headingInfo">
          <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseInfo" aria-expanded="false" aria-controls="collapseInfo">
            Подробнее о стационарности и тестах
          </button>
        </h2>
        <div id="collapseInfo" class="accordion-collapse collapse" aria-labelledby="headingInfo" data-bs-parent="#stationarityInfoAccordion">
          <div class="accordion-body">
            <h6>Что такое стационарность?</h6>
            <p>Временной ряд называется стационарным, если его статистические свойства не меняются со временем:</p>
              <ul>
                <li>Постоянное среднее значение</li>
                <li>Постоянная дисперсия</li>
                <li>Автокорреляция зависит только от временного лага</li>
              </ul>
            
            <h6>Тесты стационарности</h6>
            <p><strong>ADF (Augmented Dickey-Fuller)</strong>: Проверяет нулевую гипотезу о наличии единичного корня (нестационарность). Если p-value ≤ 0.05, ряд считается стационарным.</p>
            <p><strong>KPSS (Kwiatkowski-Phillips-Schmidt-Shin)</strong>: Проверяет нулевую гипотезу о стационарности. Если p-value > 0.05, ряд считается стационарным.</p>
            
            <h6>Как обеспечить стационарность?</h6>
            <ul>
              <li><strong>Дифференцирование</strong>: Вычисление разницы между последовательными значениями (удаляет тренд)</li>
              <li><strong>Сезонное дифференцирование</strong>: Вычисление разницы между значениями с сезонным лагом (удаляет сезонность)</li>
              <li><strong>Логарифмирование</strong>: Применение логарифма к значениям (стабилизирует дисперсию)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  .stationarity-guide {
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .card-header {
    border-radius: 8px 8px 0 0;
  }
  
  .btn-close-white {
    filter: brightness(0) invert(1);
  }
  
  .accordion-button:not(.collapsed) {
    background-color: #f8f9fa;
    color: #0d6efd;
  }
  
  .accordion-button:focus {
    box-shadow: none;
    border-color: rgba(0, 0, 0, 0.125);
  }
  
  .alert {
    display: flex;
    align-items: center;
  }
</style>